import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { interval, Subject, Subscription } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import {
  LLM_TOSITE_LUKU_MALLIT,
  LLM_TOSITE_LUKU_PUTKEN_NIMI,
  LLM_TOSITE_LUKU_PUTKEN_OLETUSMALLI,
  LLM_TOSITE_LUKU_PUTKET,
  LlmTositeLukuAjoRow,
  LlmTositeLukuMalli,
  LlmTositeLukuPromptRow,
  LlmTositeLukuPutki,
  LlmTositeLukuTestisetti,
  LlmTositeLukuTulosRow,
  TOSITTEEN_TIEDOT_KENTAT,
  TositteenTiedotKentta,
} from '../../../_jaettu-lemonator/model/llm-tosite-luku-testaus'
import { LlmTositeLukuMetatiedotService } from '../../../_jaettu-lemonator/service/llm-tosite-luku-metatiedot.service'
import { callLlmTositeLukuTestaus } from './llm-tosite-luku-testaus.client'

@Component({
  selector: 'app-tosite-luku-ajot',
  templateUrl: './tosite-luku-ajot.component.html',
  styleUrls: ['./tosite-luku-ajot.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TositeLukuAjotComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _pollingSub: Subscription | null = null
  private _lataaAjotKaynnissa = false

  readonly kentat = TOSITTEEN_TIEDOT_KENTAT
  readonly kentatIlmanTyyppia = TOSITTEEN_TIEDOT_KENTAT.filter(kentta => kentta !== 'tyyppi')
  readonly kenttaOtsikot: Record<TositteenTiedotKentta, string> = {
    paivamaara: 'Päivämäärä',
    summa: 'Summa',
    toimittaja: 'Toimittaja',
    businessId: 'Y-tunnus / VAT',
    businessIdType: 'Tunnuksen tyyppi',
    toimittajaAvain: 'Toimittaja-avain',
    tyyppi: 'Tyyppi',
  }
  readonly mallit = LLM_TOSITE_LUKU_MALLIT
  readonly putket = LLM_TOSITE_LUKU_PUTKET
  readonly putkenNimi = LLM_TOSITE_LUKU_PUTKEN_NIMI

  readonly setit = signal<LlmTositeLukuTestisetti[]>([])
  readonly promptVersiot = signal<LlmTositeLukuPromptRow[]>([])

  /** mat-select ei näytä null-arvoista optiota valittuna, joten "uusin" ja "kaikki" ovat merkkijonovakioita. */
  readonly uusinPrompt = 'uusin' as const
  readonly kaikkiSetit = 'kaikki' as const

  settiKentta = form(signal<string | null>(null))
  valittuPutki: LlmTositeLukuPutki = 'lemonator'
  putkiKentta = form(signal<LlmTositeLukuPutki>(this.valittuPutki))
  malliKentta = form(signal<LlmTositeLukuMalli>(LLM_TOSITE_LUKU_PUTKEN_OLETUSMALLI.lemonator))
  promptVersioKentta = form(signal<number | 'uusin'>(this.uusinPrompt))
  readonly kaynnistetaan = signal(false)

  readonly ajot = signal<LlmTositeLukuAjoRow[]>([])
  readonly ajotLataa = signal(false)
  suodatusSettiAvain: string | null = null
  suodatusKentta = form(signal<string>(this.kaikkiSetit))
  readonly kasiteltyLkm = signal<ReadonlyMap<string, number>>(new Map())

  readonly tuloksetAjoAvain = signal<string | null>(null)
  readonly tulokset = signal<LlmTositeLukuTulosRow[]>([])
  readonly tuloksetLataa = signal(false)
  /** Jälkikäsittelyajon tuloksiin: lähdeajon tulokset testitapauksittain, jotta mallin vastaus näkyy lopullisen arvon rinnalla. */
  readonly lahdeTulokset = signal<ReadonlyMap<string, LlmTositeLukuTulosRow>>(new Map())
  readonly naytaPromptAvaimet = signal<ReadonlySet<string>>(new Set())
  readonly avattuTulosAvain = signal<string | null>(null)
  readonly raakaVastaustaLadataan = signal(false)
  readonly jalkikasitellaan = signal<ReadonlySet<string>>(new Set())

  constructor(
    private _firebase: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
    private _metatiedotService: LlmTositeLukuMetatiedotService,
  ) {
    arvonMuutokset(this.putkiKentta)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(putki => this.vaihdaPutki(putki))
    arvonMuutokset(this.suodatusKentta)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(valinta => this.vaihdaSuodatus(valinta))
  }

  async ngOnInit() {
    await Promise.all([this._lataaSetit(), this._lataaPromptVersiot(), this._lataaAjot()])
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
    this._lopetaPolling()
  }

  async vaihdaPutki(putki: LlmTositeLukuPutki) {
    this.valittuPutki = putki
    asetaArvo(this.malliKentta, LLM_TOSITE_LUKU_PUTKEN_OLETUSMALLI[putki])
    asetaArvo(this.promptVersioKentta, this.uusinPrompt)
    this.promptVersiot.set([])
    await this._lataaPromptVersiot()
  }

  async aloitaTestiAjo() {
    if (!this.settiKentta().value() || this.kaynnistetaan()) {
      return
    }
    this.kaynnistetaan.set(true)
    try {
      const promptVersio = this.promptVersioKentta().value()
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'aloitaTestiAjo', {
        settiAvain: this.settiKentta().value(),
        malli: this.malliKentta().value(),
        putki: this.valittuPutki,
        ...(promptVersio !== this.uusinPrompt ? { promptVersio } : {}),
      })
      if (vastaus.e === 'no-testitapauksia') {
        this._snackbar.open('Setti on tyhjä', 'OK', { duration: 5000 })
        return
      }
      if (vastaus.e === 'no-prompt') {
        this._snackbar.open('Putkelle ei löydy promptia. Tallenna prompti Prompti-välilehdellä.', 'OK', { duration: 8000 })
        return
      }
      if (vastaus.e === 'invalid-putki') {
        this._snackbar.open('Promptiversio ei kuulu valittuun putkeen', 'OK', { duration: 5000 })
        return
      }
      if (vastaus.e || !vastaus.ajoAvain) {
        this._snackbar.open(`Testiajoa ei voitu aloittaa: ${vastaus.e ?? 'tuntematon virhe'}`, 'OK', { duration: 5000 })
        return
      }
      this._snackbar.open('Testiajo aloitettu', 'OK', { duration: 3000 })
      await this._lataaAjot()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Testiajoa ei voitu aloittaa', 'OK', { duration: 5000 })
    } finally {
      this.kaynnistetaan.set(false)
    }
  }

  async ajaJalkikasittely(ajo: LlmTositeLukuAjoRow) {
    if (this.jalkikasitellaan().has(ajo.avain)) {
      return
    }
    this.jalkikasitellaan.update(avaimet => new Set(avaimet).add(ajo.avain))
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'ajaJalkikasittely', { ajoAvain: ajo.avain })
      if (vastaus.e || !vastaus.ajoAvain) {
        this._snackbar.open(`Jälkikäsittely epäonnistui: ${vastaus.e ?? 'tuntematon virhe'}`, 'OK', { duration: 8000 })
        return
      }
      this._snackbar.open('Jälkikäsittely valmis', 'OK', { duration: 3000 })
      await this._lataaAjot()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Jälkikäsittely epäonnistui', 'OK', { duration: 8000 })
    } finally {
      this.jalkikasitellaan.update(avaimet => new Set([...avaimet].filter(avain => avain !== ajo.avain)))
    }
  }

  kentatAjolle(ajo: LlmTositeLukuAjoRow) {
    return ajo.putki === 'lemonaid' ? this.kentat : this.kentatIlmanTyyppia
  }

  lahdeajonAloitus(ajo: LlmTositeLukuAjoRow): string | null {
    return this.ajot().find(a => a.avain === ajo.lahdeAjoAvain)?.aloitettu ?? null
  }

  async vaihdaSuodatus(valinta: string) {
    this.suodatusSettiAvain = valinta === this.kaikkiSetit ? null : valinta
    await this._lataaAjot()
  }

  onAjoPaneelAuki(ajo: LlmTositeLukuAjoRow) {
    if (ajo.tila === 'kaynnissa' || this.tuloksetAjoAvain() === ajo.avain) {
      return
    }
    this._haeTulokset(ajo.avain)
  }

  onAjoPaneelKiinni(avain: string) {
    if (this.tuloksetAjoAvain() === avain) {
      this.tuloksetAjoAvain.set(null)
      this.tulokset.set([])
      this.avattuTulosAvain.set(null)
    }
    this.naytaPromptAvaimet.update(avaimet => new Set([...avaimet].filter(a => a !== avain)))
  }

  togglePrompt(avain: string) {
    if (this.naytaPromptAvaimet().has(avain)) {
      this.naytaPromptAvaimet.update(avaimet => new Set([...avaimet].filter(a => a !== avain)))
    } else {
      this.naytaPromptAvaimet.update(avaimet => new Set(avaimet).add(avain))
    }
  }

  async toggleTulos(tulos: LlmTositeLukuTulosRow) {
    if (this.avattuTulosAvain() === tulos.avain) {
      this.avattuTulosAvain.set(null)
      return
    }
    this.avattuTulosAvain.set(tulos.avain)
    if (tulos.raakaVastaus !== undefined) {
      return
    }
    this.raakaVastaustaLadataan.set(true)
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeTuloksenRaakaVastaus', { tulosAvain: tulos.avain })
      this._asetaRaakaVastaus(tulos, vastaus.e ? null : vastaus.raakaVastaus)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._asetaRaakaVastaus(tulos, null)
    } finally {
      this.raakaVastaustaLadataan.set(false)
    }
  }

  private _asetaRaakaVastaus(tulos: LlmTositeLukuTulosRow, raakaVastaus: string | null) {
    this.tulokset.update(tulokset => tulokset.map(rivi => (rivi.avain === tulos.avain ? { ...rivi, raakaVastaus } : rivi)))
  }

  taydellisetProsentti(ajo: LlmTositeLukuAjoRow): number | null {
    if (ajo.taydellisetOsumat === null || !ajo.tositteita) {
      return null
    }
    return (ajo.taydellisetOsumat / ajo.tositteita) * 100
  }

  arvoTekstiksi(tulos: LlmTositeLukuTulosRow, kentta: TositteenTiedotKentta, puoli: 'odotetut' | 'luetut' | 'lahde'): string {
    const tiedot = puoli === 'odotetut' ? tulos.odotetutTiedot : puoli === 'luetut' ? tulos.luetutTiedot : (this._lahdeTulos(tulos)?.luetutTiedot ?? null)
    if (!tiedot) {
      return '—'
    }
    const arvo = tiedot[kentta]
    if (arvo === null || arvo === undefined || arvo === '') {
      return 'null'
    }
    return kentta === 'summa' ? Number(arvo).toFixed(2) : String(arvo)
  }

  kaikkiOikein(tulos: LlmTositeLukuTulosRow, ajo: LlmTositeLukuAjoRow): boolean {
    return this._metatiedotService.onkoTaydellinenOsuma(this.kentatAjolle(ajo).map(kentta => tulos.osumat[kentta]))
  }

  osumanVari(tulos: LlmTositeLukuTulosRow, kentta: TositteenTiedotKentta): string {
    return this._osumanVari(tulos.osumat[kentta])
  }

  lahteenOsumanVari(tulos: LlmTositeLukuTulosRow, kentta: TositteenTiedotKentta): string {
    return this._osumanVari(this._lahdeTulos(tulos)?.osumat[kentta] ?? null)
  }

  private _lahdeTulos(tulos: LlmTositeLukuTulosRow) {
    return tulos.testitapausAvain ? this.lahdeTulokset().get(tulos.testitapausAvain) : undefined
  }

  private _osumanVari(osuma: boolean | null) {
    return osuma === null ? '#999' : osuma ? '#2e7d32' : '#c62828'
  }

  private async _lataaSetit() {
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeSetit', {})
      if (!vastaus.e) {
        this.setit.set(vastaus.setit)
        if (!this.settiKentta().value() && this.setit().length) {
          asetaArvo(this.settiKentta, this.setit()[0].avain)
        }
      }
    } catch (err) {
      this._errorHandler.handleError(err)
    }
  }

  private async _lataaPromptVersiot() {
    const putki = this.valittuPutki
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haePromptHistoria', { putki })
      // Nopeasti vaihdettaessa aiemman putken vastaus voi saapua viimeisenä.
      if (!vastaus.e && putki === this.valittuPutki) {
        this.promptVersiot.set(vastaus.rivit)
      }
    } catch (err) {
      this._errorHandler.handleError(err)
    }
  }

  private async _lataaAjot(naytaSpinneri = true) {
    if (this._lataaAjotKaynnissa) {
      return
    }
    this._lataaAjotKaynnissa = true
    if (naytaSpinneri) {
      this.ajotLataa.set(true)
    }
    try {
      const vastaus = await callLlmTositeLukuTestaus(this._firebase, 'haeTestiajot', this.suodatusSettiAvain ? { settiAvain: this.suodatusSettiAvain } : {})
      if (vastaus.e) {
        this._snackbar.open('Testiajojen haku epäonnistui', 'OK', { duration: 5000 })
        return
      }
      const avoinnaOllutAjoKaynnissa = this.ajot().find(a => a.avain === this.tuloksetAjoAvain())?.tila === 'kaynnissa'
      this.ajot.set(vastaus.ajot)
      if (avoinnaOllutAjoKaynnissa && this.tuloksetAjoAvain() && this.ajot().find(a => a.avain === this.tuloksetAjoAvain())?.tila !== 'kaynnissa') {
        this._haeTulokset(this.tuloksetAjoAvain())
      }
      const kaynnissa = this.ajot().filter(a => a.tila === 'kaynnissa')
      if (kaynnissa.length) {
        this._aloitaPolling()
        const edistymiset = await Promise.all(
          kaynnissa.map(async a => {
            const v = await callLlmTositeLukuTestaus(this._firebase, 'haeTestiajonEdistyminen', { ajoAvain: a.avain })
            return { avain: a.avain, lkm: v.kasiteltyLkm ?? 0 }
          }),
        )
        this.kasiteltyLkm.update(lkmt => {
          const uudet = new Map(lkmt)
          for (const { avain, lkm } of edistymiset) {
            uudet.set(avain, lkm)
          }
          return uudet
        })
      } else {
        this._lopetaPolling()
      }
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._lataaAjotKaynnissa = false
      if (naytaSpinneri) {
        this.ajotLataa.set(false)
      }
    }
  }

  private async _haeTulokset(ajoAvain: string) {
    this.tuloksetAjoAvain.set(ajoAvain)
    this.tuloksetLataa.set(true)
    this.avattuTulosAvain.set(null)
    this.lahdeTulokset.set(new Map())
    const lahdeAjoAvain = this.ajot().find(a => a.avain === ajoAvain)?.lahdeAjoAvain ?? null
    try {
      const [vastaus, lahdeVastaus] = await Promise.all([
        callLlmTositeLukuTestaus(this._firebase, 'haeTulokset', { ajoAvain }),
        lahdeAjoAvain ? callLlmTositeLukuTestaus(this._firebase, 'haeTulokset', { ajoAvain: lahdeAjoAvain }) : null,
      ])
      if (vastaus.e || lahdeVastaus?.e) {
        this._snackbar.open('Tulosten haku epäonnistui', 'OK', { duration: 5000 })
        return
      }
      this.tulokset.set(vastaus.tulokset)
      const lahdeTulokset = new Map<string, LlmTositeLukuTulosRow>()
      for (const lahdeTulos of lahdeVastaus?.tulokset ?? []) {
        if (lahdeTulos.testitapausAvain) {
          lahdeTulokset.set(lahdeTulos.testitapausAvain, lahdeTulos)
        }
      }
      this.lahdeTulokset.set(lahdeTulokset)
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.tuloksetLataa.set(false)
    }
  }

  private _aloitaPolling() {
    if (this._pollingSub) {
      return
    }
    this._pollingSub = interval(15000)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        this._lataaAjot(false)
      })
  }

  private _lopetaPolling() {
    if (this._pollingSub) {
      this._pollingSub.unsubscribe()
      this._pollingSub = null
    }
  }
}
